import {
   Component,
   effect,
   inject,
   OnDestroy,
   OnInit,
   signal,
   untracked,
   ViewEncapsulation
} from '@angular/core'
import { NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router'
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { EMPTY, Observable, Subject, catchError, switchMap, take, takeUntil } from 'rxjs'
import { NgSelectModule } from '@ng-select/ng-select'
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker'
import {
   AccountsService,
   AppSignals,
   AuthService,
   CommonService,
   SubscriptionsService
} from '@appShared/services'
import { AppFacade } from '@appShared/services/app.facade'
import { EducationLevels, EducationLevel_ } from '@appShared/services/lookup/[CodeGen]/education-level.domain'
import { IProfile } from '@appShared/interfaces/[Model-based]/profile.interface'
import { IProfileSubmission } from '@appShared/interfaces/[Model-based]/profile-submission.interface'
import { IUser } from '@appShared/interfaces/[Interface-based]/user.interface'
import { IContact } from '@appShared/interfaces/[Model-based]/contact.interface'
import { InstitutionType_, InstitutionTypes } from '@appShared/services/lookup/[CodeGen]/institution-type.domain'
import { ButtonComponent } from '@appShared/components/button/button.component'
import { ConfirmTemplateDirective, ConfirmModalComponent } from '@appShared/components/confirm-modal-and-service'
import { SITE_DIRECTORY_ROUTES } from '@appShared/site-directory.routes'
import * as _ from 'lodash'

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.less'],
    encapsulation: ViewEncapsulation.None,
    imports: [NgClass, FormsModule, ReactiveFormsModule, BsDatepickerModule, NgSelectModule, ButtonComponent, ConfirmTemplateDirective, ConfirmModalComponent]
})

export class RegistrationComponent implements OnInit {
   private _commonService = inject(CommonService)
   private _appFacade = inject(AppFacade)
   private _subscriptionsService = inject(SubscriptionsService)
   private _accountsService = inject(AccountsService)
   private _activatedRoute = inject(ActivatedRoute)
   private _router = inject(Router)
   private _appSignals = inject(AppSignals)
   private _authService = inject(AuthService)


   private _routes = SITE_DIRECTORY_ROUTES
   private _user: IUser
   profileContact: IContact
   private _token: string

   isLoading = signal<boolean>(true)
   isSubmitting = signal<boolean>(false)

   profile: IProfile
   // formgroup/fields
   profileSubmissionForm: FormGroup
   datePickerBaseConfig: Partial<BsDatepickerConfig> = {}
   dateOfBirthPicker: FormControl
   dateOfBirthMinDate: Date
   dateOfBirthMaxDate: Date
   educationLevelSelect: FormControl
   educationLevels = EducationLevels.filter(level => level.code > EducationLevel_.Eight)
   // https://egghead.io/lessons/angular-create-a-formcontrol-dynamically-with-reactive-forms-in-angular
   institutionTypeSelectFieldName = 'institutionTypeCode';
   institutionTypeSelect: any = null;
   institutionTypes = InstitutionTypes.filter(type => [
      InstitutionType_.Public,
      InstitutionType_.Private,
      InstitutionType_.Homeschool].includes(type.code))
   mouseoverProfileSubmissionSubmit: boolean
   errorMessage: string

   canEditProfileInfo: boolean

   constructor(
   ) {
      this._token = this._activatedRoute.snapshot.queryParamMap.get('token') || ''

      if (!this._token) {
         this._router.navigate([this._routes.uri])
         return
      }

      effect(() => {
         const currentUser = this._appSignals.currentUser()

         untracked(() => {
            this._user = currentUser
            this.profileContact = currentUser?.profile?.contact

            this._setProfileData()
            this.isLoading.set(false)
         })
      })
   }

   ngOnInit(): void {
      this.datePickerBaseConfig = this._commonService.datePickerBaseConfig
      let currentDate = this._commonService.dateTime.moment().startOf('day')
      this.dateOfBirthMinDate = currentDate.clone().add(-50, 'years')._d
      this.dateOfBirthMaxDate = currentDate.clone().add(-10, 'years')._d
      /* Add "Not Specified" (0) option at end of list */
      this.educationLevels.push(EducationLevels.find(level => level.code >= EducationLevel_.NotSpecified))
      this._createFormGroup()
   }

   /*
   * private methods
   * */
   private _setProfileData() {
      this.profile = this._user?.profile || this._accountsService.getNewProfile()

      const educationLevelCode = this.profile?.educationLevelCode
      this._commonService.setFormControlSelect(
         educationLevelCode,
         this.educationLevels,
         this.educationLevelSelect,
         null,
         true/* allowZero */
      )
      this.educationLevelSelect.updateValueAndValidity()

      const institutionTypeCode = this.profile.institutionTypeCode
      this._commonService.setDynamicFormControlSelect(
         this,
         'institutionTypeSelect',
         this.profileSubmissionForm.controls[this.institutionTypeSelectFieldName],
         this.institutionTypes,
         institutionTypeCode,
         null,
         false/* allowZero */
      )

      const dob = this.profile.dateOfBirth
      if (dob) {
         this.dateOfBirthPicker.setValue(this.profile.dateOfBirth)
      }

      this.profileSubmissionForm.updateValueAndValidity()
   }

   private _createFormGroup(): void {
      this.dateOfBirthPicker = new FormControl(null)
      this.educationLevelSelect = new FormControl(null, Validators.required)

      this.profileSubmissionForm = new FormGroup({
         dateOfBirthPicker: this.dateOfBirthPicker,
         educationLevelSelect: this.educationLevelSelect,
         [this.institutionTypeSelectFieldName]: new FormControl(
            null,
            Validators.required
         )
      })
   }

   /*
   * public methods
   * */

   competeRegistration() {
      if (this.profileSubmissionForm.valid) {

         this.isSubmitting.set(true)

         const profileSubmissionValues = this.profileSubmissionForm.value
         const isLocalIdentity = this.profile?.info?.isLocalIdentity
         const profileContact = this.profile.contact
         const firstName = profileContact?.firstName
         const lastName = profileContact?.lastName
         const emailAddress = profileContact.emailAddress
         const nickname = profileContact.info.nickname
         const userLogonName = isLocalIdentity
            ? nickname
            : emailAddress
         const educationLevelCode: EducationLevel_ = profileSubmissionValues.educationLevelSelect?.code

         const profileSubmission = {
            firstName,
            lastName,
            userLogonName,
            dateOfBirth: this._commonService.dateTime.formatDate(
               profileSubmissionValues.dateOfBirthPicker
            ),
            /*only set to education to form value it actually changed*/
            educationLevelCode: (educationLevelCode || educationLevelCode === EducationLevel_.NotSpecified)
               ? educationLevelCode
               : null,
            institutionTypeCode: profileSubmissionValues.institutionTypeCode
         } as IProfileSubmission

         let logError = (err): Observable<never> => {
            this.isSubmitting.set(false)
            this.errorMessage = err
            console.error(`Error Occurred: ${err}`)
            return EMPTY
         }

         this._subscriptionsService
            .registerProfileWithToken(this._token, profileSubmission).pipe(
               take(1),
               switchMap(() => this._appFacade.refreshCurrentUser()),
               catchError(error => logError(error))
            )
            .subscribe((user) => {
               this._commonService.messageUser(`<div class="text-center fs-5 text-uppercase">User registered!</div>`)

               this._router.navigate([`/${this._routes.content.uri}`])
            })
      }
   }

   logout(): void {
      this._authService.logoutRegistrationRedirect(this._token)
   }
}
